import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, Theme, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import {
  CampaignFunnelCustomField,
  CampaignFunnelRecord,
  CampaignFunnelState,
} from "../../services/campaignFunnel.service";

type RecordPatch = Partial<
  Pick<CampaignFunnelRecord, "customerName" | "dealValue" | "notes" | "customFields">
>;

type Props = {
  campaignName: string;
  totalLeads: number;
  funnelState: CampaignFunnelState;
  disabled?: boolean;
  onAddStage: (stageName: string) => string | null;
  onChangeRecordStage: (recordId: string, stageId: string) => void;
  onRemoveRecord: (recordId: string) => void;
  onUpdateRecord: (recordId: string, patch: RecordPatch) => void;
  onCreateCustomField: () => CampaignFunnelCustomField;
};

const getStageAccent = (stageId: string, theme: Theme) => {
  if (stageId === "customers") {
    return theme.palette.success.main;
  }

  if (stageId === "interested") {
    return theme.palette.primary.main;
  }

  return "#64748B";
};

const escapeCsvValue = (value: unknown) => {
  let text = String(value ?? "");
  if (/^[=+\-@]/.test(text)) {
    text = `'${text}`;
  }
  return `"${text.replace(/"/g, '""')}"`;
};

export default function CampaignFunnelPanel({
  campaignName,
  totalLeads,
  funnelState,
  disabled = false,
  onAddStage,
  onChangeRecordStage,
  onRemoveRecord,
  onUpdateRecord,
  onCreateCustomField,
}: Props) {
  const theme = useTheme();
  const [selectedStageId, setSelectedStageId] = useState<string>(
    funnelState.stages[0]?.id || "interested",
  );
  const [newStageName, setNewStageName] = useState("");

  useEffect(() => {
    if (funnelState.stages.some((stage) => stage.id === selectedStageId)) {
      return;
    }

    setSelectedStageId(funnelState.stages[0]?.id || "interested");
  }, [funnelState.stages, selectedStageId]);

  const stageCounts = funnelState.records.reduce<Record<string, number>>(
    (acc, record) => {
      acc[record.stageId] = (acc[record.stageId] || 0) + 1;
      return acc;
    },
    {},
  );

  const selectedStage =
    funnelState.stages.find((stage) => stage.id === selectedStageId) ||
    funnelState.stages[0];

  const selectedStageRecords = selectedStage
    ? funnelState.records.filter((record) => record.stageId === selectedStage.id)
    : [];

  const handleAddStage = () => {
    const createdStageId = onAddStage(newStageName);
    if (!createdStageId) {
      return;
    }

    setSelectedStageId(createdStageId);
    setNewStageName("");
  };

  const updateCustomField = (
    record: CampaignFunnelRecord,
    fieldId: string,
    patch: Partial<CampaignFunnelCustomField>,
  ) => {
    onUpdateRecord(record.id, {
      customFields: record.customFields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              ...patch,
            }
          : field,
      ),
    });
  };

  const addCustomField = (record: CampaignFunnelRecord) => {
    onUpdateRecord(record.id, {
      customFields: [...record.customFields, onCreateCustomField()],
    });
  };

  const removeCustomField = (record: CampaignFunnelRecord, fieldId: string) => {
    onUpdateRecord(record.id, {
      customFields: record.customFields.filter((field) => field.id !== fieldId),
    });
  };

  const interestedCount = stageCounts.interested || 0;
  const customerCount = stageCounts.customers || 0;

  const exportCrmCsv = () => {
    const stageNames = new Map(
      funnelState.stages.map((stage) => [stage.id, stage.name]),
    );
    const customHeaders = Array.from(
      new Set(
        funnelState.records.flatMap((record) =>
          record.customFields
            .map((field) => field.label.trim())
            .filter(Boolean),
        ),
      ),
    );
    const headers = [
      "Name",
      "Phone",
      "List",
      "CRM stage",
      "Source status",
      "Deal value",
      "Notes",
      "WhatsApp message ID",
      ...customHeaders,
    ];
    const rows = funnelState.records.map((record) => {
      const customValues = new Map(
        record.customFields.map((field) => [field.label.trim(), field.value]),
      );
      return [
        record.customerName,
        record.phone,
        record.listName,
        stageNames.get(record.stageId) || record.stageId,
        record.sourceStatus,
        record.dealValue,
        record.notes,
        record.waMessageId || "",
        ...customHeaders.map((header) => customValues.get(header) || ""),
      ];
    });
    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const filename = (campaignName || "campaign")
      .trim()
      .replace(/[^a-z0-9_-]+/gi, "-")
      .replace(/^-+|-+$/g, "") || "campaign";
    anchor.href = url;
    anchor.download = `${filename}-crm.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.25 },
        borderRadius: 2,
        borderColor: alpha(theme.palette.text.primary, 0.08),
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "background.paper",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={1}
            >
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 0.5, opacity: 0.7 }}>
                  CRM funnel
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                  Campaign pipeline
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Chip
                  icon={<GroupOutlinedIcon />}
                  label={`${totalLeads} leads`}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={exportCrmCsv}
                  disabled={disabled || funnelState.records.length === 0}
                  sx={{ borderRadius: 2.5, bgcolor: "#FFFFFF" }}
                >
                  Export CSV
                </Button>
              </Stack>
            </Stack>

            <Typography variant="body2" sx={{ opacity: 0.78 }}>
              Convert message responses into a clean sales workflow. Move contacts
              to interested, customers, or any custom stage you create.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1.25,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.14),
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                    }}
                  >
                    <EmojiObjectsOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Interested
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      {interestedCount}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.success.main, 0.18),
                  bgcolor: alpha(theme.palette.success.main, 0.06),
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.success.main, 0.14),
                      color: theme.palette.success.main,
                    }}
                  >
                    <MonetizationOnOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Customers
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      {customerCount}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            size="small"
            fullWidth
            label="Add a new pipeline stage"
            placeholder="Qualified, Demo booked, Proposal sent..."
            value={newStageName}
            disabled={disabled}
            onChange={(event) => setNewStageName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAddStage();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddStage}
            disabled={disabled}
            sx={{
              minWidth: { sm: 150 },
              borderRadius: 2.5,
              boxShadow: "none",
            }}
          >
            Add stage
          </Button>
        </Stack>

        <Divider />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 0.9fr) minmax(0, 1.25fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
              Funnel stages
            </Typography>

            {funnelState.stages.map((stage) => {
              const count = stageCounts[stage.id] || 0;
              const rate = totalLeads ? Math.round((count / totalLeads) * 100) : 0;
              const accent = getStageAccent(stage.id, theme);
              const isActive = selectedStage?.id === stage.id;

              return (
                <ButtonBase
                  key={stage.id}
                  disabled={disabled}
                  onClick={() => setSelectedStageId(stage.id)}
                  sx={{
                    width: "100%",
                    textAlign: "left",
                    borderRadius: 2,
                    display: "block",
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: isActive
                        ? alpha(accent, 0.35)
                        : alpha(theme.palette.text.primary, 0.08),
                      bgcolor: isActive ? alpha(accent, 0.06) : "#FFFFFF",
                      boxShadow: isActive
                        ? `0 12px 24px ${alpha(accent, 0.12)}`
                        : "none",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              bgcolor: accent,
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {stage.name}
                          </Typography>
                        </Stack>
                        <Chip
                          size="small"
                          label={count}
                          sx={{
                            fontWeight: 700,
                            bgcolor: alpha(accent, 0.12),
                            color: accent,
                          }}
                        />
                      </Stack>

                      <LinearProgress
                        variant="determinate"
                        value={Math.max(rate, count > 0 ? 4 : 0)}
                        sx={{
                          height: 8,
                          borderRadius: 999,
                          bgcolor: alpha(accent, 0.12),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            bgcolor: accent,
                          },
                        }}
                      />

                      <Typography variant="caption" sx={{ color: "#64748B" }}>
                        {count} of {totalLeads} leads ({rate}%)
                      </Typography>
                    </Stack>
                  </Paper>
                </ButtonBase>
              );
            })}
          </Stack>

          <Stack spacing={1.5}>
            {selectedStage ? (
              <>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: alpha(
                      getStageAccent(selectedStage.id, theme),
                      0.2,
                    ),
                    bgcolor: alpha(getStageAccent(selectedStage.id, theme), 0.04),
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Box>
                        <Typography variant="overline" sx={{ letterSpacing: 0.5, opacity: 0.68 }}>
                          Current stage
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                          {campaignName || "Campaign"} - {selectedStage.name}
                        </Typography>
                      </Box>
                      <Chip
                        icon={<SellOutlinedIcon />}
                        label={`${selectedStageRecords.length} lead${
                          selectedStageRecords.length === 1 ? "" : "s"
                        }`}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      />
                    </Stack>

                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                      Keep customer context, notes, deal value, and custom CRM
                      metadata attached to this stage.
                    </Typography>
                  </Stack>
                </Paper>

                {selectedStageRecords.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      textAlign: "center",
                      borderStyle: "dashed",
                      borderColor: alpha(theme.palette.text.primary, 0.14),
                      bgcolor: "#FBFDFF",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      No leads in {selectedStage.name} yet
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
                      Use the CRM stage dropdown in the message activity table to
                      move campaign leads here.
                    </Typography>
                  </Paper>
                ) : (
                  selectedStageRecords.map((record) => (
                    <Paper
                      key={record.id}
                      variant="outlined"
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        borderColor: alpha(theme.palette.text.primary, 0.08),
                        boxShadow: "none",
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {record.customerName || record.phone}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ mt: 0.75, flexWrap: "wrap", gap: 1 }}
                            >
                              <Chip size="small" label={record.phone} />
                              {record.listName && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`List: ${record.listName}`}
                                />
                              )}
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Source: ${record.sourceStatus || "Unknown"}`}
                              />
                            </Stack>
                          </Box>

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                          >
                            <TextField
                              select
                              size="small"
                              label="Stage"
                              value={record.stageId}
                              disabled={disabled}
                              onChange={(event) =>
                                onChangeRecordStage(record.id, event.target.value)
                              }
                              sx={{
                                minWidth: { sm: 165 },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2.5,
                                },
                              }}
                            >
                              {funnelState.stages.map((stage) => (
                                <MenuItem key={stage.id} value={stage.id}>
                                  {stage.name}
                                </MenuItem>
                              ))}
                            </TextField>
                            <Button
                              color="inherit"
                              disabled={disabled}
                              onClick={() => onRemoveRecord(record.id)}
                              sx={{ borderRadius: 2.5 }}
                            >
                              Remove
                            </Button>
                          </Stack>
                        </Stack>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Customer name"
                            value={record.customerName}
                            disabled={disabled}
                            onChange={(event) =>
                              onUpdateRecord(record.id, {
                                customerName: event.target.value,
                              })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                              },
                            }}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Deal value"
                            placeholder="INR 25,000 or annual plan"
                            value={record.dealValue}
                            disabled={disabled}
                            onChange={(event) =>
                              onUpdateRecord(record.id, {
                                dealValue: event.target.value,
                              })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2.5,
                              },
                            }}
                          />
                        </Stack>

                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={3}
                          label="Notes and next action"
                          placeholder="Capture what this lead wants, any objections, meeting notes, next follow-up, or close plan..."
                          value={record.notes}
                          disabled={disabled}
                          onChange={(event) =>
                            onUpdateRecord(record.id, {
                              notes: event.target.value,
                            })
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2.5,
                            },
                          }}
                        />

                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 2.5,
                            bgcolor: "#FCFDFE",
                            borderColor: alpha(theme.palette.text.primary, 0.08),
                          }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Custom CRM fields
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                disabled={disabled}
                                onClick={() => addCustomField(record)}
                                sx={{ borderRadius: 2.5 }}
                              >
                                Add field
                              </Button>
                            </Stack>

                            {record.customFields.length === 0 && (
                              <Typography variant="caption" sx={{ color: "#64748B" }}>
                                Add fields for city, budget, sales owner, meeting
                                date, source campaign angle, or any CRM detail you
                                want to preserve here.
                              </Typography>
                            )}

                            {record.customFields.map((field) => (
                              <Stack
                                key={field.id}
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                              >
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Field name"
                                  value={field.label}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateCustomField(record, field.id, {
                                      label: event.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2.5,
                                    },
                                  }}
                                />
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="Value"
                                  value={field.value}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateCustomField(record, field.id, {
                                      value: event.target.value,
                                    })
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2.5,
                                    },
                                  }}
                                />
                                <IconButton
                                  aria-label="Delete custom field"
                                  disabled={disabled}
                                  onClick={() => removeCustomField(record, field.id)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Stack>
                            ))}
                          </Stack>
                        </Paper>
                      </Stack>
                    </Paper>
                  ))
                )}
              </>
            ) : (
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                No CRM stages available.
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
